import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Bot, Key, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface Config { id?: number; provider: string; model: string; isActive: boolean; hasKey?: boolean; systemPrompt?: string | null; dailyCreditCap?: number }

export function AiChatbotPanel() {
  const { user } = useAuth();
  const isSuperAdmin = user?.kind === 'user' && user.userType === 'SUPER_ADMIN';

  const qc = useQueryClient();
  const { data: configs = [] } = useQuery({
    queryKey: ['ai-chatbot-config'],
    queryFn: async () => (await api.get<Config[]>('/ai-chatbot/config')).data,
  });

  const [provider, setProvider] = useState<'gemini' | 'deepseek'>('gemini');
  const [model, setModel] = useState('gemini-2.0-flash');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [dailyCreditCap, setDailyCreditCap] = useState(50);
  const [systemPrompt, setSystemPrompt] = useState('');

  useEffect(() => {
    const c = configs.find((x) => x.provider === provider);
    if (c) {
      setModel(c.model);
      setIsActive(c.isActive);
      setDailyCreditCap(c.dailyCreditCap ?? 50);
      setSystemPrompt(c.systemPrompt ?? '');
      setApiKey(c.hasKey ? '••••••••••••' : '');
    } else {
      setModel(provider === 'gemini' ? 'gemini-2.0-flash' : 'deepseek-chat');
      setApiKey('');
      setIsActive(true);
    }
  }, [provider, configs]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/ai-chatbot/config', {
      provider, model,
      apiKey: apiKey.includes('•') ? undefined : apiKey,
      baseUrl: baseUrl || undefined,
      isActive,
      dailyCreditCap,
      systemPrompt: systemPrompt || undefined,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-chatbot-config'] }); toast.success('AI config saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  if (!isSuperAdmin) {
    return <div className="p-6 text-sm text-slate-500">SUPER_ADMIN only.</div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-4">
        <Bot size={22} className="text-emerald-700" />
        <h2 className="text-xl font-bold">AI Chatbot Configuration</h2>
      </div>
      <p className="text-sm text-slate-600 mb-5">
        Configure Gemini (Google) or DeepSeek to power the in-app chatbot. The active provider is used by both admin and merchant chat. Merchants must also be enabled individually with a daily credit limit on their Merchant Detail page.
      </p>

      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          {(['gemini', 'deepseek'] as const).map((p) => (
            <button key={p} onClick={() => setProvider(p)}
              className={`px-4 py-2 rounded-lg text-sm border ${provider === p ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {p === 'gemini' ? 'Google Gemini' : 'DeepSeek'}
            </button>
          ))}
        </div>

        <div>
          <label className="text-xs text-slate-500 block mb-1">Model</label>
          <input value={model} onChange={(e) => setModel(e.target.value)}
            placeholder={provider === 'gemini' ? 'gemini-2.0-flash' : 'deepseek-chat'}
            className="w-full border rounded px-3 py-2 text-sm" />
          <div className="text-[11px] text-slate-400 mt-1">
            {provider === 'gemini' ? 'Latest Flash model recommended: gemini-2.0-flash' : 'Default: deepseek-chat (V4). For Pro use deepseek-reasoner.'}
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 block mb-1 flex items-center gap-1"><Key size={12} /> API Key</label>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={apiKey.includes('•') ? 'Enter new key to replace, or leave masked' : 'Paste your API key'}
            className="w-full border rounded px-3 py-2 text-sm font-mono" />
        </div>

        {provider === 'deepseek' && (
          <div>
            <label className="text-xs text-slate-500 block mb-1">Base URL (optional)</label>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.deepseek.com"
              className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Default daily credit cap (per merchant)</label>
            <input type="number" min={0} value={dailyCreditCap} onChange={(e) => setDailyCreditCap(Math.max(0, Number(e.target.value)))}
              className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span>Active provider</span>
            </label>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 block mb-1">Extra instructions (optional)</label>
          <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder='e.g. "Answer like an operations agent. Prefer figures over prose."'
            rows={4} className="w-full border rounded px-3 py-2 text-xs font-mono" />
          <div className="text-[11px] text-slate-400 mt-1">
            Added <span className="font-medium">on top of</span> the built-in prompt, which teaches the model your database schema — it is not a replacement.
            Anything typed here shapes tone and context; it cannot change which tables are readable or how columns are named.
          </div>
        </div>

        <div className="flex items-center justify-end pt-2">
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <Save size={14} /> Save
          </button>
        </div>
      </div>

      <div className="mt-5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          <p>The chatbot translates plain-English/Hindi/Hinglish questions into a read-only SQL query against a whitelist of business tables. Tracking events, credentials, audit logs and the chat log itself are EXCLUDED. Only SELECT queries run.</p>
          <p className="mt-1">Multi-tenant safety: when a merchant asks, the system auto-adds a <code className="font-mono bg-white px-1 rounded">WHERE merchant_id = &lt;them&gt;</code> filter to every query.</p>
        </div>
      </div>
    </div>
  );
}
